import {
  FlexColumn,
  FlexRow,
  LabeledInput,
  Loading,
  StyledBanner,
  StyledButton,
  useLoading,
  useModal,
} from '@gorila-shared-ui/components';
import { Checkbox } from 'baseui/checkbox';
import { HeadingSmall, ParagraphMedium } from 'baseui/typography';
import { useEffect } from 'react';
import { areEqual } from 'react-window';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../constants/app';
import { useCamera } from '../../hooks/useCamera';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import {
  deleteDeliveryFence,
  getDeliveryFence,
  postDeliveryFence,
  updateDeliveryFence,
} from '../../services/deliveryFenceService';
import { apiParamsState } from '../../storage/app';
import {
  editingDeliveryFenceState,
  editSelectedDeliveryFenceState,
  selectedDeliveryFenceIdState,
  selectedDeliveryFenceState,
  updateDeliveryFencesState,
} from '../../storage/deliveryFences';
import { DeliveryFenceRequest } from '../../types/fence';
import { FloatingCardContent } from '../layout/cards/FloatingCardContent';
import ConfirmModal from '../shared/ConfirmModal';

export function DeliveryFenceDetail() {
  const { theme, css } = useStyles();
  const { fitToLngLats } = useCamera();
  const { loading: saving, startLoading: startSaving, stopLoading: stopSaving } = useLoading();
  const { openModal, closeModal, showModal } = useModal();
  const { showFailFeedback, showDeletionFeedback, showPositiveFeedback } = useFeedback();
  const [edit, setEdit] = useRecoilState(editSelectedDeliveryFenceState);
  const [editingDeliveryFence, setEditingDeliveryFence] = useRecoilState(editingDeliveryFenceState);
  const [selectedDeliveryFence, setSelectedDeliveryFence] = useRecoilState(selectedDeliveryFenceState);
  const [selectedDeliveryFenceId, setSelectedDeliveryFenceId] = useRecoilState(selectedDeliveryFenceIdState);
  const { loading, startLoading, stopLoading } = useLoading(!!selectedDeliveryFenceId);
  const apiParams = useRecoilValue(apiParamsState);
  const isNewDeliveryFence = !loading && editingDeliveryFence?._id === '';
  const canEdit = !isNewDeliveryFence;
  const resetEdit = useResetRecoilState(editSelectedDeliveryFenceState);
  const resetEditingDeliveryFence = useResetRecoilState(editingDeliveryFenceState);
  const resetSelectedDeliveryFence = useResetRecoilState(selectedDeliveryFenceState);
  const resetSelectedDeliveryFenceId = useResetRecoilState(selectedDeliveryFenceIdState);
  const setUpdateDeliveryFences = useSetRecoilState(updateDeliveryFencesState);

  const loadDeliveryFenceData = async (silently = false) => {
    if (!selectedDeliveryFenceId || !apiParams) return;
    if (!silently) startLoading();
    const { deliveryFence, error } = await getDeliveryFence(
      selectedDeliveryFenceId,
      apiParams.clientId,
      apiParams.subClientId,
    );
    if (!error && deliveryFence) {
      setEditingDeliveryFence({
        ...deliveryFence,
        clientId: deliveryFence.client,
        subClientId: deliveryFence.subClient,
      });
      setSelectedDeliveryFence(deliveryFence);
    }
    stopLoading();
  };

  useEffect(() => {
    if (selectedDeliveryFenceId) {
      loadDeliveryFenceData();
    } else {
      setSelectedDeliveryFence(undefined);
    }

    return () => {
      resetEditingDeliveryFence();
      resetEdit();
    };
  }, [selectedDeliveryFenceId]);

  useEffect(() => {
    return () => {
      resetSelectedDeliveryFence();
      resetEdit();
    };
  }, []);

  useEffect(() => {
    if (isNewDeliveryFence) {
      onEdit();
    }
  }, [isNewDeliveryFence]);

  useEffect(() => {
    if (!!editingDeliveryFence?.polygon?.coordinates[0].length) {
      fitToLngLats(editingDeliveryFence.polygon.coordinates[0]);
    }
  }, [selectedDeliveryFenceId, editingDeliveryFence?.polygon, fitToLngLats]);

  if (editingDeliveryFence === undefined) return null;

  const canSubmit = !!editingDeliveryFence.name && !!editingDeliveryFence.polygon;
  const hasUpdates = !areEqual(
    {
      name: selectedDeliveryFence?.name,
      polygon: selectedDeliveryFence?.polygon,
      active: selectedDeliveryFence?.active,
      geoReference: selectedDeliveryFence?.geoReference,
    },
    {
      name: editingDeliveryFence?.name,
      polygon: editingDeliveryFence?.polygon,
      active: editingDeliveryFence?.active,
      geoReference: editingDeliveryFence?.geoReference,
    },
  );

  const onDeleteFence = async () => {
    if (!apiParams || selectedDeliveryFenceId === undefined) return;
    const { error } = await deleteDeliveryFence(apiParams.clientId, apiParams.subClientId, selectedDeliveryFenceId);
    if (!error) {
      showDeletionFeedback(FEEDBACK.deleted(FEEDBACK_PREFIXES.deliveryFence, selectedDeliveryFence?.name));
      setUpdateDeliveryFences('update');
      resetSelectedDeliveryFenceId();
      setSelectedDeliveryFence(undefined);
      closeModal();
    } else {
      showFailFeedback(error || FEEDBACK.failedDeletion(FEEDBACK_PREFIXES.deliveryFence, selectedDeliveryFence?.name));
    }
  };

  const onInputChange = (value: string | boolean | number | null, field: keyof DeliveryFenceRequest) => {
    setEditingDeliveryFence((prev) => ({ ...prev!, [field]: value }));
  };

  const onSaveFence = async () => {
    startSaving();
    if (!apiParams) return;
    const { id, error } = isNewDeliveryFence
      ? await postDeliveryFence(editingDeliveryFence)
      : await updateDeliveryFence(editingDeliveryFence);
    if (!error && id) {
      if (isNewDeliveryFence) setSelectedDeliveryFenceId(id);
      showPositiveFeedback(
        isNewDeliveryFence
          ? FEEDBACK.created(FEEDBACK_PREFIXES.deliveryFence)
          : FEEDBACK.edited(FEEDBACK_PREFIXES.deliveryFence, editingDeliveryFence.name),
      );
      setUpdateDeliveryFences(id);
      await loadDeliveryFenceData(true);
      onStopEditing();
    } else {
      showFailFeedback(
        error || isNewDeliveryFence
          ? FEEDBACK.failedCreation(FEEDBACK_PREFIXES.deliveryFence)
          : FEEDBACK.failedEdition(FEEDBACK_PREFIXES.deliveryFence, selectedDeliveryFence?.name),
      );
    }
    stopSaving();
  };

  const onEdit = () => {
    setEdit(true);
  };

  const onStopEditing = () => {
    resetEdit();
    resetEditingDeliveryFence();
  };

  return (
    <FloatingCardContent
      flexible
      gap={theme.sizing.scale600}
    >
      <HeadingSmall margin={0}>
        {isNewDeliveryFence ? 'Nuevo punto de entrega' : selectedDeliveryFence?.name}
      </HeadingSmall>
      {loading && <Loading />}
      {!loading && (
        <>
          <FlexColumn classNames={css({ flex: 1 })}>
            <LabeledInput
              label="Nombre"
              value={editingDeliveryFence.name}
              onChange={(value) => onInputChange(value, 'name')}
              disabled={!edit}
              required
            />
            <LabeledInput
              label="Georeferencia"
              value={editingDeliveryFence.geoReference ?? ''}
              onChange={(value) => onInputChange(value, 'geoReference')}
              disabled={!edit}
            />
            <Checkbox
              checked={editingDeliveryFence.active}
              onChange={(e) => onInputChange(e.target.checked, 'active')}
              disabled={!edit}
            >
              Activo
            </Checkbox>
            {!editingDeliveryFence.polygon && (
              <StyledBanner>Selecciona un punto o genera un cerco para delimitar la entrega.</StyledBanner>
            )}
          </FlexColumn>
          <FlexRow
            classNames={`${css({
              alignItems: 'center',
              justifyContent: isNewDeliveryFence ? 'flex-end' : 'space-between',
            })}`}
          >
            {!isNewDeliveryFence && (
              <StyledButton
                kind="tertiary"
                onClick={openModal}
              >
                Eliminar
              </StyledButton>
            )}
            <FlexRow gap={theme.sizing.scale300}>
              <StyledButton
                kind="tertiary"
                onClick={() => {
                  if (canEdit && edit) {
                    onStopEditing();
                  } else {
                    setSelectedDeliveryFence(undefined);
                    resetSelectedDeliveryFenceId();
                  }
                }}
              >
                {canEdit && edit ? 'Cancelar' : 'Cerrar'}
              </StyledButton>
              {edit && (
                <StyledButton
                  onClick={onSaveFence}
                  isLoading={saving}
                  disabled={!canSubmit || !hasUpdates}
                >
                  Guardar
                </StyledButton>
              )}
              {!edit && (
                <StyledButton
                  onClick={onEdit}
                  kind="tertiary"
                >
                  Editar
                </StyledButton>
              )}
            </FlexRow>
          </FlexRow>
        </>
      )}
      <ConfirmModal
        isOpen={showModal}
        onCancel={closeModal}
        onConfirm={onDeleteFence}
        confirmText="Eliminar"
        description={<ParagraphMedium>¿Desea eliminar "{selectedDeliveryFence?.name}"?</ParagraphMedium>}
      />
    </FloatingCardContent>
  );
}
